import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  subtitleNode?: ReactNode;
  /** Let the subtitle wrap instead of truncating (use when the copy must stay readable on mobile). */
  subtitleWrap?: boolean;
  action?: ReactNode;
  backLink?: { label: string; to: string };
  className?: string;
}

export function PageHeader({ title, subtitle, subtitleNode, subtitleWrap, action, backLink, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-4', className)}>
      {backLink && (
        <Link
          to={backLink.to}
          // Below md only the arrow shows, so the label is the link's accessible name. The 44px hit area is
          // padding pulled back by the same margin, so the arrow stays aligned with the content's left edge.
          aria-label={backLink.label}
          className="-ml-2 flex w-fit min-h-[44px] min-w-[44px] items-center gap-1 rounded-md px-2 text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden md:inline">{backLink.label}</span>
        </Link>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-foreground truncate md:text-xl">
            {title}
          </h1>
          {subtitleNode ? (
            <p className={cn('mt-0.5 text-sm text-muted-foreground', !subtitleWrap && 'truncate')}>
              {subtitleNode}
            </p>
          ) : subtitle && (
            <p className={cn('mt-0.5 text-sm text-muted-foreground', !subtitleWrap && 'truncate')}>
              {subtitle}
            </p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}
